import * as React from 'react';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';

import { GRID_DATA_TYPES } from 'constants/index';
import { customStatusBar, customDefaultColDefs } from '../helpers/gridDefaults';
import { GridWrapper } from '../styled/Grid';
import { Page } from 'components/Layout';
import { FilterChangedEvent, FirstDataRenderedEvent, GridApi, GridReadyEvent, themeMaterial } from 'ag-grid-enterprise';
import { useCallback } from 'react';

interface Props extends AgGridReactProps {
    autosize?: boolean;
    gridClassName?: string;
    hideStatusBar?: boolean;
    columns?: any;
    filters?: {};
    height?: string;
    sorts?: any[];
    sizeToFit?: boolean;
    selectFirstRow?: boolean;
    title?: string;
    disclaimer?: string | React.ReactNode;
    setGridApi?: (gridApi: GridApi) => void;
    saveGridData?: (dataType: string, data?: any) => void;
    masterDetail?: boolean;
    detailCellRenderer?: any;
    detailCellRendererParams?: any;
    dense?: boolean;
    dataIdField?: string;
}

const defaultProps = {
    height: '72vh',
    gridClassName: 'ag-theme-material',
};

const LrGrid: React.FunctionComponent<Props> = (props) => {
    const {
        aggFuncs,
        gridClassName,
        hideStatusBar,
        autosize,
        columns,
        filters,
        icons,
        sorts,
        sizeToFit,
        setGridApi,
        selectFirstRow,
        height,
        defaultColDef,
        onFirstDataRendered,
        onGridReady,
        statusBar,
        saveGridData,
        title,
        disclaimer,
        gridOptions,
        treeData,
        getDataPath,
        treeDataParentIdField,
        masterDetail,
        detailCellRenderer,
        detailCellRendererParams,
        dense,
        dataIdField,
        ...restOfProps
    } = props;

    const autosizeCols = (gridParams: FirstDataRenderedEvent) => {
        const { api } = gridParams;
        api.autoSizeAllColumns();
    };

    const restoreGrid = (gridParams: GridReadyEvent) => {
        const { api } = gridParams;
        try {
            columns && api.applyColumnState(columns);
        } catch (e) {
            /* Note:
             * nothing to do,
             * this strange error pops when a grid is mounted, then immediately unmounted, usually as a result of timing related issues involving a "loading" boolean controlling component inclusion
             **/
        }

        try {
            filters && api.setFilterModel(filters);
        } catch (e) {
            // see above
        }
        // try {
        //     sorts && !!sorts.length && api.setSortModel(sorts);
        // } catch (e) {
        //     // see above
        // }
        // after setting the model on the API it can mess up the aggregations, the following refreshes any
        api.refreshClientSideRowModel('aggregate');
    };

    const handleFirstDataRendered = (gridParams: FirstDataRenderedEvent) => {
        selectFirstRow && gridParams.api.forEachNode((n) => (n.rowIndex ? 0 : n.setSelected(true)));
        autosize && !columns && autosizeCols(gridParams);
        sizeToFit && gridParams.api.sizeColumnsToFit();
        onFirstDataRendered && onFirstDataRendered(gridParams);
    };

    const handleGridReady = (gridParams: GridReadyEvent) => {
        setGridApi && setGridApi(gridParams.api);
        restoreGrid(gridParams);
        onGridReady && onGridReady(gridParams);
    };

    const handleColChange = (event: any) => {
        const { api } = event;
        saveGridData && saveGridData(GRID_DATA_TYPES.COLUMNS, api.getColumnState());
    };

    const handleFilterChange = (event: FilterChangedEvent) => {
        const { api } = event;
        saveGridData && saveGridData(GRID_DATA_TYPES.FILTERS, api.getFilterModel());
    };

    const dataPath = (data: any) => {
        if (!data.hierarchy) {
            return [];
        }
        return data.hierarchy.split('|');
    };

    const getRowId = useCallback(
        (params: any) => {
            return dataIdField ? params.data[dataIdField] : null;
        },
        [dataIdField]
    );

    const theme = {
        height,
        dense,
    };

    // Grid Customized Defaults
    const customDataPath = treeData ? (treeDataParentIdField ? undefined : getDataPath || dataPath) : getDataPath;

    // TODO - the custom icons were causing parent organizations with no children to be indented,
    // making it look like they were children of the row above them.
    // Removing for now until this can be resolved.
    const customIcons = icons;
    // const customIcons = icons || {
    //     groupExpanded: '<i class="fa fa-caret-down" />',
    //     groupContracted: '<i class="fa fa-caret-right" />'
    // };

    const renderGrid = () => {
        return (
            <GridWrapper theme={theme} className={gridClassName}>
                <AgGridReact
                    theme={'legacy'}
                    columnMenu={'legacy'}
                    defaultColDef={defaultColDef || customDefaultColDefs}
                    masterDetail={masterDetail}
                    detailCellRendererParams={detailCellRendererParams}
                    detailCellRenderer={detailCellRenderer}
                    onGridReady={handleGridReady}
                    onFirstDataRendered={handleFirstDataRendered}
                    statusBar={hideStatusBar ? undefined : statusBar || customStatusBar}
                    scrollbarWidth={15}
                    icons={customIcons}
                    aggFuncs={aggFuncs}
                    gridOptions={customGridOptions}
                    treeData={treeData}
                    getDataPath={customDataPath}
                    getRowId={dataIdField ? getRowId : undefined}
                    treeDataParentIdField={treeDataParentIdField}
                    rowDragManaged={treeData}
                    {...restOfProps}
                />
            </GridWrapper>
        );
    };

    const customGridOptions = !saveGridData
        ? gridOptions
        : {
              ...gridOptions,
              onColumnVisible: handleColChange,
              onColumnPinned: handleColChange,
              onColumnResized: handleColChange,
              onColumnMoved: handleColChange,
              onFilterChanged: handleFilterChange,
              onSortChanged: handleColChange,
          };

    return (
        <>
            {title && (
                <Page title={title} disclaimer={disclaimer}>
                    {renderGrid()}
                </Page>
            )}
            {!title && renderGrid()}
        </>
    );
};

LrGrid.defaultProps = defaultProps;
export default React.memo(LrGrid);
